import { toast as sonner } from 'sonner';

type ToastMessage = Parameters<typeof sonner.error>[0];
type ToastOptions = Parameters<typeof sonner.error>[1];

/**
 * Sonner, with one behavioural change: an error toast is keyed by its own text, so
 * raising the same message twice REPLACES the first rather than stacking a copy on it.
 *
 * Why this is needed rather than "just don't toast twice": a single failure legitimately
 * reaches the toast layer from two directions. `queryClient`'s MutationCache raises one
 * for every failed mutation, and a component's own `onError` often raises another to say
 * something more specific — and neither knows about the other. On top of that, two
 * queries failing on the same underlying call (a poll plus a fetch, say) produce two
 * identical messages from two unrelated places.
 *
 * Deduping on the message means the user sees one notification per distinct problem,
 * whatever combination of layers noticed it. Different messages still show separately,
 * because those are genuinely different things to tell someone.
 *
 * Success/info/warning are passed straight through: those fire from one deliberate place
 * and repeating an action SHOULD confirm it each time.
 */
/** Plain `toast(...)` — an undecorated notification, e.g. one carrying an Undo action. */
function base(message: ToastMessage, options?: ToastOptions) {
  return sonner(message, options);
}

export const toast = Object.assign(base, {
  error: (message: ToastMessage, options?: ToastOptions) =>
    sonner.error(message, {
      // Only dedupe what we can compare. A ReactNode message has no stable identity,
      // so it keeps sonner's default behaviour rather than collapsing unrelated toasts.
      ...(typeof message === 'string' ? { id: `err:${message}` } : {}),
      ...options,
    }),

  success: (message: ToastMessage, options?: ToastOptions) => sonner.success(message, options),
  info: (message: ToastMessage, options?: ToastOptions) => sonner.info(message, options),
  warning: (message: ToastMessage, options?: ToastOptions) => sonner.warning(message, options),
  message: (message: ToastMessage, options?: ToastOptions) => sonner.message(message, options),
  loading: (message: ToastMessage, options?: ToastOptions) => sonner.loading(message, options),
  dismiss: (id?: string | number) => sonner.dismiss(id),
});
